// Vitals + event timeline for the debrief. Pure SVG, no chart library.
// Three traces on the same time axis (HR, SpO2, EtCO2) with action markers overlaid.
// Hover/click a marker -> details. Scrub bar shows current cursor on all traces.

import { useMemo, useState } from 'react';

interface Vitals {
  t_ms: number;
  rhythm: string;
  hr: number;
  sbp: number;
  dbp: number;
  spo2: number;
  etco2_x10: number;
  cpr_active: boolean;
  cpr_quality_pct: number;
  current_phase: string;
}

interface Event {
  t_ms: number;
  action_type: string;
  actor_role?: string | null;
  action_payload_json?: any;
}

interface Props {
  vitals: Vitals[];
  events: Event[];
}

// Visual constants
const W = 1000;
const H = 280;
const PAD_L = 50;
const PAD_R = 12;
const PAD_T = 12;
const PAD_B = 38;
const LANE_GAP = 4;
const N_LANES = 3;

// Action -> marker classification
const MARKER_STYLES: Record<string, { color: string; label: string; emoji: string }> = {
  cpr_pulse:               { color: '#1aff9c', label: 'CPR pulse',                emoji: '·'  },
  start_compressions:      { color: '#1aff9c', label: 'CPR started',              emoji: '▶'  },
  stop_compressions:       { color: '#888',    label: 'CPR stopped',              emoji: '■'  },
  rhythm_check:            { color: '#5be1ff', label: 'Rhythm check',             emoji: '◊'  },
  pulse_check_carotid:     { color: '#5be1ff', label: 'Pulse check',              emoji: '◊'  },
  charge_defib:            { color: '#ffe066', label: 'Charge defib',             emoji: '⚡' },
  shock:                   { color: '#ff4d4d', label: 'Shock delivered',         emoji: '⚡' },
  drug_admin:              { color: '#ffb84d', label: 'Drug',                    emoji: '💉' },
  insert_igel:             { color: '#5be1ff', label: 'i-gel inserted',          emoji: '🫁' },
  insert_lma:              { color: '#5be1ff', label: 'LMA inserted',            emoji: '🫁' },
  intubate_attempt:        { color: '#5be1ff', label: 'Intubation',              emoji: '🫁' },
  bvm_ventilation:         { color: '#5be1ff', label: 'BVM breath',              emoji: '🫁' },
  call_for_help:           { color: '#0d8081', label: 'Called for help',         emoji: '📞' },
  declare_4hs_4ts:         { color: '#0d8081', label: 'Declared 4Hs/4Ts',        emoji: '✓'  },
  declare_differential:    { color: '#0d8081', label: 'Declared differential',   emoji: '✓'  },
  team_callout:            { color: '#0d8081', label: 'Closed-loop callout',     emoji: '🗣' },
  ecg_12_lead:             { color: '#a78bfa', label: '12-lead ECG',             emoji: '📈' },
  abg:                     { color: '#a78bfa', label: 'ABG',                    emoji: '🩸' },
  bedside_echo:            { color: '#a78bfa', label: 'Bedside echo',            emoji: '📡' },
  system_harm_event:       { color: '#ff4d4d', label: 'HARM EVENT',              emoji: '!'  },
  system_rhythm_change:    { color: '#ff4d4d', label: 'Rhythm degenerated',      emoji: '!'  },
};

const FALLBACK_MARKER = { color: '#888', label: '', emoji: '·' };

function lookupMarker(at: string) {
  return MARKER_STYLES[at] || { ...FALLBACK_MARKER, label: at.replace(/_/g, ' ') };
}

export default function VitalsTimeline({ vitals, events }: Props) {
  const [hover, setHover] = useState<{ x: number; t_ms: number; v: Vitals } | null>(null);

  const totalDuration = useMemo(() => {
    const last = vitals.length > 0 ? vitals[vitals.length - 1]!.t_ms : 0;
    const lastEvent = events.length > 0 ? events[events.length - 1]!.t_ms : 0;
    return Math.max(last, lastEvent, 60_000); // at least 1 min span so chart never collapses
  }, [vitals, events]);

  const xOf = (t_ms: number) => PAD_L + (t_ms / totalDuration) * (W - PAD_L - PAD_R);

  // Three lane rectangles
  const laneH = (H - PAD_T - PAD_B) / N_LANES - LANE_GAP;
  const laneRect = (i: number) => ({
    x: PAD_L,
    y: PAD_T + i * (laneH + LANE_GAP),
    w: W - PAD_L - PAD_R,
    h: laneH,
  });
  const LANES: Array<{ key: keyof Vitals; label: string; color: string; min: number; max: number; format: (v: number) => string }> = [
    { key: 'hr',         label: 'HR (bpm)',     color: '#1aff9c', min: 0,   max: 200, format: (v) => String(v) },
    { key: 'spo2',       label: 'SpO₂ (%)',     color: '#ffe066', min: 0,   max: 100, format: (v) => String(v) },
    { key: 'etco2_x10',  label: 'EtCO₂ (mmHg)', color: '#5be1ff', min: 0,   max: 600, format: (v) => (v / 10).toFixed(0) },
  ];

  // Build polylines per lane
  const polylines = LANES.map((lane, i) => {
    const rect = laneRect(i);
    if (vitals.length === 0) return { lane, points: '' };
    const pts: string[] = [];
    for (const v of vitals) {
      const raw = (v[lane.key] as unknown) as number;
      const norm = Math.max(0, Math.min(1, (raw - lane.min) / (lane.max - lane.min)));
      const x = xOf(v.t_ms);
      const y = rect.y + rect.h - norm * rect.h;
      pts.push(`${x.toFixed(1)},${y.toFixed(1)}`);
    }
    return { lane, points: pts.join(' '), rect };
  });

  // Time axis ticks every 60s
  const ticks: number[] = [];
  for (let t = 0; t <= totalDuration; t += 60_000) ticks.push(t);

  // Hover handler
  function onMouseMove(e: React.MouseEvent<SVGSVGElement>) {
    const rect = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * W;
    if (x < PAD_L || x > W - PAD_R) { setHover(null); return; }
    const t_ms = ((x - PAD_L) / (W - PAD_L - PAD_R)) * totalDuration;
    // Find nearest vitals snapshot
    let nearest: Vitals | null = null;
    let best = Infinity;
    for (const v of vitals) {
      const d = Math.abs(v.t_ms - t_ms);
      if (d < best) { best = d; nearest = v; }
    }
    if (nearest) setHover({ x, t_ms, v: nearest });
  }

  // Compute event positions in pixel-space and avoid label collisions
  const eventPlacements = useMemo(() => {
    const placements: Array<{ ev: Event; x: number; lane: number; marker: { color: string; label: string; emoji: string } }> = [];
    const occupied = new Set<number>();
    for (const ev of events) {
      const x = xOf(ev.t_ms);
      // pick a free row (lane) starting at the top
      let row = 0;
      while (occupied.has(Math.round(x / 18) * 100 + row) && row < 4) row++;
      occupied.add(Math.round(x / 18) * 100 + row);
      placements.push({ ev, x, lane: row, marker: lookupMarker(ev.action_type) });
    }
    return placements;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, totalDuration]);

  return (
    <div className="panel p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-mono-white/60 text-xs uppercase tracking-widest">Vitals timeline · {vitals.length} samples · {events.length} events</h3>
        <div className="flex gap-3 text-xs">
          {LANES.map((l) => (
            <span key={l.label} className="font-mono" style={{ color: l.color }}>● {l.label}</span>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ minWidth: 720 }} onMouseMove={onMouseMove} onMouseLeave={() => setHover(null)}>
          {/* Lane backgrounds + horizontal gridlines */}
          {polylines.map(({ lane, points, rect }, i) => rect ? (
            <g key={lane.label}>
              <rect x={rect.x} y={rect.y} width={rect.w} height={rect.h} fill="#0a0e14" />
              {/* y-axis label */}
              <text x={PAD_L - 6} y={rect.y + rect.h / 2 + 3} textAnchor="end" fontSize="9" fill="#a8b0c2" fontFamily="ui-monospace">{lane.label}</text>
              {/* mid-line */}
              <line x1={rect.x} y1={rect.y + rect.h / 2} x2={rect.x + rect.w} y2={rect.y + rect.h / 2} stroke="#1a212d" strokeDasharray="2 4" />
              {/* trace */}
              {points && <polyline points={points} fill="none" stroke={lane.color} strokeWidth="1.6" strokeLinejoin="round" />}
              {/* y-axis bounds */}
              <text x={PAD_L - 6} y={rect.y + 8} textAnchor="end" fontSize="8" fill="#666" fontFamily="ui-monospace">{lane.format(lane.max)}</text>
              <text x={PAD_L - 6} y={rect.y + rect.h - 1} textAnchor="end" fontSize="8" fill="#666" fontFamily="ui-monospace">{lane.format(lane.min)}</text>
            </g>
          ) : null)}

          {/* Time axis ticks */}
          {ticks.map((t) => {
            const x = xOf(t);
            return (
              <g key={t}>
                <line x1={x} y1={PAD_T} x2={x} y2={H - PAD_B} stroke="#1a212d" strokeDasharray="2 6" />
                <text x={x} y={H - PAD_B + 14} textAnchor="middle" fontSize="9" fill="#a8b0c2" fontFamily="ui-monospace">
                  {Math.floor(t / 60000)}:{String(Math.floor((t % 60000) / 1000)).padStart(2, '0')}
                </text>
              </g>
            );
          })}

          {/* Event markers */}
          {eventPlacements.map(({ ev, x, lane: row, marker }, idx) => {
            const y = H - PAD_B + 22 + row * 14;
            return (
              <g key={idx}>
                <line x1={x} y1={PAD_T} x2={x} y2={H - PAD_B} stroke={marker.color} strokeOpacity="0.25" strokeWidth="0.8" />
                <circle cx={x} cy={H - PAD_B + 24} r="3" fill={marker.color} />
                <title>{`T+${(ev.t_ms / 1000).toFixed(0)}s — ${marker.label}${ev.actor_role ? ` (${ev.actor_role})` : ''}`}</title>
              </g>
            );
          })}

          {/* Hover cursor */}
          {hover && (
            <g>
              <line x1={hover.x} y1={PAD_T} x2={hover.x} y2={H - PAD_B} stroke="#ffffff70" strokeWidth="1" />
              <rect x={Math.min(hover.x + 8, W - 180)} y={PAD_T + 4} width="170" height="78" rx="3" fill="#0a0e14" stroke="#1a212d" />
              <text x={Math.min(hover.x + 14, W - 174)} y={PAD_T + 18} fontSize="11" fill="#fff" fontFamily="ui-monospace">
                T+{(hover.t_ms / 1000).toFixed(0)}s · {hover.v.rhythm}
              </text>
              <text x={Math.min(hover.x + 14, W - 174)} y={PAD_T + 32} fontSize="10" fill="#1aff9c" fontFamily="ui-monospace">HR {hover.v.hr}</text>
              <text x={Math.min(hover.x + 14, W - 174)} y={PAD_T + 46} fontSize="10" fill="#ffe066" fontFamily="ui-monospace">SpO₂ {hover.v.spo2}%</text>
              <text x={Math.min(hover.x + 14, W - 174)} y={PAD_T + 60} fontSize="10" fill="#5be1ff" fontFamily="ui-monospace">EtCO₂ {(hover.v.etco2_x10 / 10).toFixed(0)}</text>
              <text x={Math.min(hover.x + 14, W - 174)} y={PAD_T + 74} fontSize="10" fill="#a8b0c2" fontFamily="ui-monospace">BP {hover.v.sbp}/{hover.v.dbp} · {hover.v.current_phase}</text>
            </g>
          )}
        </svg>
      </div>

      {/* Event legend / list grouped by category */}
      {events.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-mono-white/60 font-mono">
          {events.slice(0, 30).map((e, i) => {
            const m = lookupMarker(e.action_type);
            return (
              <span key={i} className="inline-flex items-center gap-1">
                <span style={{ color: m.color }}>●</span>
                <span className="text-mono-white/50">{(e.t_ms / 1000).toFixed(0)}s</span>
                <span>{m.label}</span>
              </span>
            );
          })}
          {events.length > 30 && <span className="text-mono-white/40">…and {events.length - 30} more</span>}
        </div>
      )}
    </div>
  );
}
